/**
 * Hides the Bunnings header bottom row on scroll-down and shows it on scroll-up.
 * RAF-coalesced scroll, cooldown after toggles, and asymmetric deltas avoid layout/scroll feedback loops.
 * Show/hide motion is CSS (grid-template-rows + padding); this file only sets data-bunnings-bottom.
 * Mobile search stays visible in the sticky header so row spacing and padding are preserved while scrolling.
 */

import { debounce } from '@theme/utilities';

const TOP_THRESHOLD_PX = 16;
const COLLAPSE_DELTA_PX = 10;
const EXPAND_DELTA_PX = 12;
const COOLDOWN_MS = 175;

/**
 * @param {HTMLElement} root
 */
function setHeaderHeightVar(root) {
  const nextHeight = Math.round(root.getBoundingClientRect().height);
  if (nextHeight > 0) {
    document.body.style.setProperty('--header-height', `${nextHeight}px`);
  }
}

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isElementVisible(el) {
  return getComputedStyle(el).display !== 'none';
}

/**
 * Dialog component sets body to fixed while open, and some dialogs also use html[scroll-lock].
 * Treat both as a temporary non-user scroll state.
 *
 * @returns {boolean}
 */
function isDialogScrollLockActive() {
  return document.body.style.position === 'fixed' || document.documentElement.hasAttribute('scroll-lock');
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} bottomRow
 * @param {boolean} collapsed
 * @returns {boolean}
 */
function setBottomCollapsed(root, bottomRow, collapsed) {
  const next = collapsed ? 'collapsed' : 'expanded';
  if (root.getAttribute('data-bunnings-bottom') === next) return false;

  root.setAttribute('data-bunnings-bottom', next);
  bottomRow.inert = collapsed;
  return true;
}

/**
 * @param {HTMLElement} root
 */
function initBunningsHeaderAutoHide(root) {
  const bottomRow = root.querySelector('.bunnings-header__row--bottom');
  if (!(bottomRow instanceof HTMLElement)) return;
  const bottomColumns = root.querySelector('.bunnings-header__columns--bottom');

  let lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
  /** @type {number | null} */
  let scrollRafId = null;
  let ignoreDirectionUntil = 0;
  let wasLocked = false;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const syncHeaderHeight = debounce(() => {
    setHeaderHeightVar(root);
  }, 120);

  const onResize = debounce(() => {
    if (!isElementVisible(bottomRow)) {
      root.removeAttribute('data-bunnings-bottom');
      bottomRow.inert = false;
    }
    syncHeaderHeight();
  }, 150);

  /**
   * @param {TransitionEvent} event
   */
  const onBottomTransitionEnd = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target !== bottomRow && event.target !== bottomColumns) return;
    if (
      event.propertyName !== 'grid-template-rows' &&
      event.propertyName !== 'padding-top' &&
      event.propertyName !== 'padding-bottom'
    ) {
      return;
    }
    syncHeaderHeight();
  };

  const updateScrollState = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const now = performance.now();
    const bottomRowVisible = isElementVisible(bottomRow);

    if (isDialogScrollLockActive()) {
      wasLocked = true;
      lastScrollTop = scrollTop;
      return;
    }

    if (wasLocked) {
      wasLocked = false;
      lastScrollTop = scrollTop;
      ignoreDirectionUntil = now + COOLDOWN_MS;
      return;
    }

    if (!bottomRowVisible) {
      lastScrollTop = scrollTop;
    }

    if (scrollTop <= TOP_THRESHOLD_PX) {
      if (bottomRowVisible && setBottomCollapsed(root, bottomRow, false)) {
        ignoreDirectionUntil = now + COOLDOWN_MS;
        if (prefersReducedMotion) syncHeaderHeight();
      }
      lastScrollTop = scrollTop;
      return;
    }

    if (now < ignoreDirectionUntil) {
      lastScrollTop = scrollTop;
      return;
    }

    const delta = scrollTop - lastScrollTop;

    if (delta < -EXPAND_DELTA_PX) {
      if (bottomRowVisible && setBottomCollapsed(root, bottomRow, false)) {
        ignoreDirectionUntil = now + COOLDOWN_MS;
        if (prefersReducedMotion) syncHeaderHeight();
      }
    } else if (delta > COLLAPSE_DELTA_PX) {
      if (bottomRowVisible && setBottomCollapsed(root, bottomRow, true)) {
        ignoreDirectionUntil = now + COOLDOWN_MS;
        if (prefersReducedMotion) syncHeaderHeight();
      }
    }

    lastScrollTop = scrollTop;
  };

  const onScroll = () => {
    if (scrollRafId !== null) return;
    scrollRafId = requestAnimationFrame(() => {
      scrollRafId = null;
      updateScrollState();
    });
  };

  if (isElementVisible(bottomRow)) {
    root.setAttribute('data-bunnings-bottom', 'expanded');
    bottomRow.inert = false;
  }
  setHeaderHeightVar(root);

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  bottomRow.addEventListener('transitionend', onBottomTransitionEnd);
  bottomColumns?.addEventListener('transitionend', onBottomTransitionEnd);
}

document.querySelectorAll('[data-bunnings-auto-hide-bottom]').forEach((node) => {
  if (node instanceof HTMLElement) initBunningsHeaderAutoHide(node);
});
